import { Icon } from '@toggle/design-system';
import { CatalystInsightCategory, CatalystSubcategories } from '@toggle/toggle';
import clsx from 'clsx';
import React from 'react';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useMarketCatalystDrawer } from '~/widgets/market-catalyst-drawer/useMarketCatalystDrawer';

import { CatalystInsightTooltip } from '../catalyst-insight-tooltip/CatalystInsightTooltip';
import { useCatalystDrawer } from '../useCatalystDrawer';
import styles from './CatalystInsightIcon.module.scss';
import { getCatalystInsightIconProps } from './utils';

const COMPANY_CATALYSTS = 'company';

export interface CatalystInsightIconProps {
  catalystInsightId: string;
  subCategory?: CatalystSubcategories;
  size?: number;
  category: CatalystInsightCategory;
  enableHover?: boolean;
  isBordered?: boolean;
  identifier?: string;
  customSize?: { width: number; height: number };
  isDisabled?: boolean;
  source: PosthogTrackingSource;
  isClickable?: boolean;
}

export const CatalystInsightIcon = ({
  catalystInsightId,
  category,
  subCategory,
  size,
  enableHover = true,
  isBordered = true,
  identifier,
  customSize,
  isDisabled,
  source,
  isClickable = true,
}: CatalystInsightIconProps) => {
  const { openDrawer: openCompanyCatalystDrawer } = useCatalystDrawer();
  const { openDrawer: openMarketCatalystDrawer } = useMarketCatalystDrawer();
  const isCompanyCatalyst = category === COMPANY_CATALYSTS;
  const iconProps = getCatalystInsightIconProps({
    isBordered,
    isDisabled,
    isCompanyCatalyst,
  });
  const { trackInsightIconClicked } = usePosthogEvents();

  const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();

    if (!identifier) {
      return;
    }

    trackInsightIconClicked(
      source,
      isCompanyCatalyst ? 'company_catalyst' : 'market_catalyst'
    );

    if (isCompanyCatalyst) {
      openCompanyCatalystDrawer({
        type: category,
        entityTag: identifier,
        insightId: catalystInsightId,
        source,
      });
    } else if (subCategory) {
      openMarketCatalystDrawer({
        type: subCategory,
        themeId: identifier,
        insightId: catalystInsightId,
        source,
      });
    }
  };

  return (
    <TooltipContainer
      label={
        <CatalystInsightTooltip
          category={category}
          subCategory={subCategory}
          iconProps={iconProps}
        />
      }
      placement="top-start"
      offsetY={-5}
      offsetX={8}
      hasMaxWidth={false}
      disabled={!enableHover || isDisabled}
      inPortal
      withSafePolygon
    >
      <Icon
        {...iconProps}
        {...(customSize
          ? { width: customSize.width, height: customSize.height }
          : { size })}
        className={clsx(styles.styledIcon, {
          [styles.enableHover]: enableHover && isClickable,
          [styles.isDisabled]: isDisabled,
        })}
        onClick={isClickable ? handleClick : undefined}
      />
    </TooltipContainer>
  );
};
